import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { serializeColorFunc } from './useFractalWorker';
import { paletteColor } from './fractalPalette';
import { appendHistory, captureHistoryImage } from './fractalHistory';
import { cameraFromRanges, numberCameraFromSnapshot } from './fractalCamera';
import { loadDeepZoom } from './loadDeepZoom';
import { useFractalRenderer } from './useFractalRenderer';

const DEEP_ITERATIONS = 4096;
const WHEEL_ZOOM_RATE = 0.002;
const MAX_WHEEL_ZOOM_STEP = 0.18;
const TRACKPAD_PINCH_ZOOM_RATE = 0.008;
const MAX_TRACKPAD_PINCH_ZOOM_STEP = 0.6;
const progressSuffix = value => Number.isFinite(value)
  ? ` ${Math.round(Math.max(0, Math.min(1, value)) * 100)}%` : '';
function deepStatusText(status, canRequestDetail, width, height) {
  if (!status) return null;
  const { phase, progress } = status;
  if (phase === 'reference') {
    return progress?.phase === 'validating' ? 'Checking view…' : 'Calculating view…';
  }
  if (phase === 'rendering' || phase === 'preview') {
    const preview = status.width < width || status.height < height;
    return `${preview ? 'Building preview' : 'Refining detail'}…${progressSuffix(progress)}`;
  }
  if (phase === 'repairing') return 'Recovering detail…';
  if (phase === 'complete') return canRequestDetail ? `Detail at ${status.iterationBudget || DEEP_ITERATIONS} iterations.` : null;
  return status.message || (phase === 'loading' ? 'Loading deep zoom…' : phase === 'limited' ? 'More detail is available.'
    : phase === 'error' ? 'Deep zoom could not be rendered.' : 'Refining detail…');
}
const fittedAspect = (policy, width, height) => width === policy.width && height === policy.height
  ? policy.aspect : policy.aspect * (width / policy.width) / (height / policy.height);
function inverseViewTransform(camera, source) {
  const { scale, offset } = camera.viewTransform(source);
  const result = { scaleX: 1 / scale[0], scaleY: 1 / scale[1], x: -offset[0] / scale[0], y: -offset[1] / scale[1] };
  return Object.values(result).every(Number.isFinite) ? result : null;
}

function transformPreview(canvas, transform) {
  if (!canvas || !transform) return;
  canvas.style.transformOrigin = '0 0';
  canvas.style.transform = `translate(${transform.x * 100}%, ${transform.y * 100}%) scale(${transform.scaleX}, ${transform.scaleY})`;
}
const applyOperation = (camera, operation, width, height) => operation.type === 'zoom'
  ? camera.zoomAt(...operation.args, width, height, DEEP_ITERATIONS)
  : operation.type === 'aspect' ? camera.fitAspect(...operation.args, width, height, DEEP_ITERATIONS)
    : camera.panPixels(...operation.args, width, height, DEEP_ITERATIONS);

function replayOperations(camera, operations, width, height) {
  let limitError = null;
  for (const operation of operations) {
    try { camera = applyOperation(camera, operation, width, height); }
    catch (error) {
      if (error.code !== 'CAMERA_PRECISION_LIMIT') throw error;
      // A rejected zoom is atomic. Later queued gestures may zoom out or pan
      // within the available precision and must still be applied in order.
      limitError = error;
    }
  }
  return { camera, limitError };
}

/** All input is expressed in screen coordinates; the camera owns world arithmetic. */
export function FractalCanvas({ width, height, xRange: initialX, yRange: initialY,
  camera: initialSnapshot, colorFunc: customColorFunc, palette, preferredRenderer = 'webgl',
  cpuReusePaletteArray = true, cpuExactPanReuse = true,
  cpuInteriorDispatch = true, onCpuFrameReady }) {
  const initialCamera = useRef(undefined);
  if (initialCamera.current === undefined) initialCamera.current = initialSnapshot ? null : cameraFromRanges(initialX, initialY);
  // Legacy callers may deliberately supply a world aspect unlike the backing
  // buffer. Retain that relationship when the output dimensions change.
  const aspectPolicyRef = useRef({ aspect: initialSnapshot?.aspect || initialCamera.current.aspect, width, height });
  const cameraRef = useRef(initialCamera.current);
  const canvasRef = useRef(null);
  const deepCanvasRef = useRef(null);
  const runtimeRef = useRef(null);
  const controllerRef = useRef(null);
  const controllerCanvasRef = useRef(null);
  const handoffRef = useRef(initialSnapshot ? { snapshot: initialSnapshot, operations: [] } : null);
  const loadGenerationRef = useRef(0);
  const mountedRef = useRef(false);
  const deepRef = useRef(!!initialSnapshot);
  const deepImageVisibleRef = useRef(false);
  const shallowPreviewVisibleRef = useRef(false);
  const handoffFadeRef = useRef(null);
  const shallowBackingRef = useRef({ width, height });
  const backingFrozenRef = useRef(false);
  const refreshShallowAfterUnfreezeRef = useRef(false);
  const [, refreshBacking] = useState(0);
  const [deep, setDeep] = useState(false);
  const [status, setStatus] = useState(null);
  const statusRef = useRef(null);
  // Native wheel and keyboard handlers may run before React commits another
  // render. History transactions therefore share synchronous camera lifetime.
  const historyRef = useRef([]);
  const [shiftPressed, setShiftPressed] = useState(false);
  const frameRef = useRef(null);
  const frameOptionsRef = useRef(null);
  const timerRef = useRef(null);
  const wheelHistoryRef = useRef(false);
  const needsRefinementRef = useRef(false);
  const zoomAnchorRef = useRef(null);
  const panRef = useRef(null);
  const pinchRef = useRef(null);
  const presentRef = useRef(null);
  const loadRef = useRef(null);
  const propsRef = useRef({ width, height });
  propsRef.current = { width, height };
  const colorFunc = useMemo(() => palette ? value => paletteColor(value, palette) : customColorFunc, [palette, customColorFunc]);
  const colorParams = useMemo(() => palette || serializeColorFunc(colorFunc), [palette, colorFunc]);
  const colorParamsRef = useRef(colorParams);
  colorParamsRef.current = colorParams;
  const imagePaletteKey = JSON.stringify(colorParams);
  const imageIdentityRef = useRef(null);
  imageIdentityRef.current = { paletteKey: imagePaletteKey, colorFunc: palette ? null : customColorFunc };
  const onFallback = useCallback(() => {
    const ranges = !deepRef.current && !handoffRef.current
      ? cameraRef.current?.toShallowRanges() : null;
    // A failed pan shader has already committed its requested camera. Ending
    // the gesture avoids applying that movement again on the replacement canvas.
    panRef.current = null;
    if (frameRef.current != null) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    frameOptionsRef.current = null;
    return ranges;
  }, []);
  const { canvasEpoch, recovering, present: presentShallow, suspend, restoreImage, canCaptureHistory, getPresentedSnapshot } = useFractalRenderer(canvasRef, {
    initialRanges: initialCamera.current?.toShallowRanges() || null,
    colorFunc, colorParams, paletteParams: palette, cpuReusePaletteArray, cpuExactPanReuse,
    cpuInteriorDispatch,
    preferredRenderer, onFallback, onCpuFrameReady,
  });
  const report = useCallback(next => {
    if (!mountedRef.current) return;
    statusRef.current = next;
    setStatus(next);
  }, []);

  const cancelFrame = useCallback(() => {
    if (frameRef.current != null) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    frameOptionsRef.current = null;
    clearTimeout(timerRef.current);
    timerRef.current = null;
    needsRefinementRef.current = false;
  }, []);

  const ensureRuntime = useCallback(async (retry = false) => {
    if (runtimeRef.current && !retry) return runtimeRef.current;
    const runtime = await loadDeepZoom({ retry });
    runtimeRef.current = runtime;
    return runtime;
  }, []);

  const present = useCallback((options = {}) => {
    if (!mountedRef.current) return;
    const camera = cameraRef.current;
    if (handoffRef.current || !camera) {
      suspend();
      return;
    }
    const { width: renderWidth, height: renderHeight } = propsRef.current;
    const isDeep = camera.needsDeep(renderWidth, renderHeight, deepRef.current);
    if (isDeep) {
      suspend();
      deepRef.current = true;
      if (!deepImageVisibleRef.current) {
        const source = getPresentedSnapshot();
        const transform = source && inverseViewTransform(camera, source);
        shallowPreviewVisibleRef.current = !!transform;
        if (transform) transformPreview(canvasRef.current, transform);
      }
      if (!runtimeRef.current) {
        handoffRef.current = { snapshot: camera.snapshot(), operations: [] };
        loadRef.current?.();
        return;
      }
      const target = deepCanvasRef.current;
      try {
        if (!controllerRef.current || controllerCanvasRef.current !== target) {
          controllerRef.current?.dispose();
          controllerCanvasRef.current = target;
          controllerRef.current = runtimeRef.current.createDeepZoomController(target, {
            performanceOptions: { tileSize: 'auto', completedFrameCache: true,
              completedFrameHistory: 2, referenceCacheCenters: 3,
              nearbyReferenceRecall: true,
              conjugateReferenceRecall: true,
              analyticInteriorTiles: true, reuseCaptured160Preview: true,
              directInteriorFrame: 'reuse' },
            onStatus: next => {
              if (deepRef.current) {
                const hasPixels = next.hasPixels || next.completedResolution > 0 ||
                  (next.phase === 'rendering' && next.progress > 0);
                if (!deepImageVisibleRef.current && hasPixels && (!shallowPreviewVisibleRef.current || next.displayReady)) {
                  deepImageVisibleRef.current = true;
                  setDeep(true);
                  clearTimeout(handoffFadeRef.current);
                  if (shallowPreviewVisibleRef.current) {
                    handoffFadeRef.current = setTimeout(() => {
                      handoffFadeRef.current = null;
                      if (mountedRef.current && deepRef.current && deepImageVisibleRef.current && canvasRef.current) {
                        canvasRef.current.style.transform = '';
                        refreshBacking(value => value + 1);
                      }
                    }, 150);
                  } else if (canvasRef.current) canvasRef.current.style.transform = '';
                }
                report(next);
              }
            },
          });
        }
        controllerRef.current.update(camera, { colorParams: colorParamsRef.current,
          interactive: !!options.interactive, width: renderWidth, height: renderHeight,
          iterationBudget: DEEP_ITERATIONS, anchor: options.anchor || zoomAnchorRef.current });
      } catch (error) {
        report({ phase: 'error', message: error.message, error });
      }
    } else {
      if (deepRef.current) controllerRef.current?.cancel();
      deepRef.current = false;
      deepImageVisibleRef.current = false;
      shallowPreviewVisibleRef.current = false;
      clearTimeout(handoffFadeRef.current);
      handoffFadeRef.current = null;
      setDeep(false);
      report(null);
      if (canvasRef.current) canvasRef.current.style.transform = '';
      if (deepCanvasRef.current) deepCanvasRef.current.style.transform = '';
      presentShallow(camera.toShallowRanges(), options);
    }
  }, [getPresentedSnapshot, presentShallow, report, suspend]);
  presentRef.current = present;

  const startHandoff = useCallback(async (retry = false) => {
    const generation = ++loadGenerationRef.current;
    report({ phase: 'loading', message: 'Loading deep zoom…' });
    try {
      const runtime = await ensureRuntime(retry);
      if (!mountedRef.current || generation !== loadGenerationRef.current) return;
      const handoff = handoffRef.current;
      if (!handoff) return;
      const { width: renderWidth, height: renderHeight } = propsRef.current;
      const base = runtime.decimalCameraFromSnapshot(handoff.snapshot,
        { width: renderWidth, height: renderHeight, iterationBudget: DEEP_ITERATIONS });
      const { camera: next, limitError } = replayOperations(base, handoff.operations, renderWidth, renderHeight);
      cameraRef.current = next;
      handoffRef.current = null;
      if (deepCanvasRef.current) deepCanvasRef.current.style.transform = '';
      presentRef.current({ interactive: needsRefinementRef.current });
      if (limitError) report({ phase: 'limited', message: limitError.message });
    } catch (error) {
      if (mountedRef.current && generation === loadGenerationRef.current) report({ phase: 'error', message: error.message, error });
    }
  }, [ensureRuntime, report]);
  loadRef.current = startHandoff;

  const queuePresent = useCallback((options = {}) => {
    needsRefinementRef.current = !!options.interactive;
    if (options.anchor) zoomAnchorRef.current = options.anchor;
    frameOptionsRef.current = options;
    if (frameRef.current != null) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      const latest = frameOptionsRef.current;
      frameOptionsRef.current = null;
      if (latest) presentRef.current(latest);
    });
  }, []);

  // During an import, retain the original bitmap and apply only its accumulated
  // screen transform. Exact camera commands are replayed, in order, on promotion.
  const previewPending = useCallback(operation => {
    const handoff = handoffRef.current;
    let transform = handoff.transform;
    if (!transform) {
      const source = getPresentedSnapshot();
      const current = cameraRef.current;
      const snapshot = current?.snapshot();
      const baseMatches = snapshot && ['centerX', 'centerY', 'spanY', 'aspect'].every(key => snapshot[key] === handoff.snapshot[key]);
      const projected = source && baseMatches ? inverseViewTransform(current, source) : null;
      transform = { ...(projected || { scaleX: 1, scaleY: 1, x: 0, y: 0 }), aspect: handoff.snapshot.aspect };
    }
    if (operation.type === 'zoom') {
      const [factor, u, v] = operation.args;
      transform.scaleX /= factor;
      transform.scaleY /= factor;
      transform.x = (transform.x - u) / factor + u;
      transform.y = (transform.y - v) / factor + v;
    } else if (operation.type === 'aspect') {
      const aspect = operation.args[0];
      const factor = transform.aspect / aspect;
      transform.scaleX *= factor;
      transform.x = (transform.x - 0.5) * factor + 0.5;
      transform.aspect = aspect;
    } else {
      const [dx, dy, cssWidth, cssHeight] = operation.args;
      transform.x -= dx / cssWidth;
      transform.y -= dy / cssHeight;
    }
    handoff.transform = transform;
    const target = deepRef.current && controllerRef.current ? deepCanvasRef.current : canvasRef.current;
    if (target === canvasRef.current) shallowPreviewVisibleRef.current = !!getPresentedSnapshot();
    transformPreview(target, transform);
  }, [getPresentedSnapshot]);

  const operate = useCallback(operation => {
    if (handoffRef.current) {
      handoffRef.current.operations.push(operation);
      previewPending(operation);
      return true;
    }
    const current = cameraRef.current;
    if (!current) return false;
    const { width: renderWidth, height: renderHeight } = propsRef.current;
    try {
      const next = applyOperation(current, operation, renderWidth, renderHeight);
      if (!runtimeRef.current && next.needsDeep(renderWidth, renderHeight, false)) {
        handoffRef.current = { snapshot: current.snapshot(), operations: [operation] };
        previewPending(operation);
        suspend();
        startHandoff();
      } else {
        // A prefetched runtime must promote the camera before the first
        // operation crossing the handoff boundary is calculated in Decimal.
        cameraRef.current = runtimeRef.current && next.needsDeep(renderWidth, renderHeight, false) && current.kind !== 'decimal'
          ? applyOperation(runtimeRef.current.decimalCameraFromSnapshot(current.snapshot(),
            { width: renderWidth, height: renderHeight, iterationBudget: DEEP_ITERATIONS }), operation, renderWidth, renderHeight)
          : next;
        if (!runtimeRef.current && next.shouldPrefetch(renderWidth, renderHeight)) ensureRuntime().catch(() => {});
      }
      return true;
    } catch (error) {
      report({ phase: 'limited', message: error.message });
      return false;
    }
  }, [ensureRuntime, previewPending, report, startHandoff, suspend]);

  const saveHistory = useCallback(() => {
    const pending = handoffRef.current;
    const snapshot = pending ? pending.snapshot : cameraRef.current?.snapshot();
    if (!snapshot) return;
    const canvas = canvasRef.current;
    const imageIsCurrent = !deepRef.current && !pending && canvas && canCaptureHistory(cameraRef.current.toShallowRanges());
    const entry = { camera: snapshot,
      pendingOperations: pending ? pending.operations.map(operation => ({ ...operation, args: [...operation.args] })) : [],
      imageData: imageIsCurrent ? captureHistoryImage(canvas) : null,
      imageWidth: canvas?.width, imageHeight: canvas?.height,
      imagePaletteKey: imageIdentityRef.current.paletteKey, imageColorFunc: imageIdentityRef.current.colorFunc };
    historyRef.current = appendHistory(historyRef.current, entry);
  }, [canCaptureHistory]);

  const finishZoom = useCallback((immediate = false) => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
    if (!needsRefinementRef.current && !frameOptionsRef.current) return;
    if (immediate) {
      cancelFrame();
      presentRef.current({ anchor: zoomAnchorRef.current });
    } else queuePresent({ anchor: zoomAnchorRef.current });
  }, [cancelFrame, queuePresent]);

  const undo = useCallback(() => {
    const entry = historyRef.current[historyRef.current.length - 1];
    if (!entry) return;
    cancelFrame();
    panRef.current = pinchRef.current = null;
    wheelHistoryRef.current = false;
    loadGenerationRef.current += 1;
    controllerRef.current?.cancel();
    historyRef.current = historyRef.current.slice(0, -1);
    const runtime = runtimeRef.current;
    let limitError = null;
    if (runtime) {
      const base = runtime.decimalCameraFromSnapshot(entry.camera, { width, height, iterationBudget: DEEP_ITERATIONS });
      const replay = replayOperations(base, entry.pendingOperations || [], width, height);
      cameraRef.current = replay.camera;
      limitError = replay.limitError;
      handoffRef.current = null;
    } else if (entry.pendingOperations?.length || Number(entry.camera.spanY) === 0 || entry.camera.precisionDigits > 16) {
      handoffRef.current = { snapshot: entry.camera, operations: [...(entry.pendingOperations || []),
        { type: 'aspect', args: [fittedAspect(aspectPolicyRef.current, width, height)] }] };
      startHandoff();
      return;
    } else {
      cameraRef.current = numberCameraFromSnapshot(entry.camera);
      handoffRef.current = null;
    }
    const aspect = fittedAspect(aspectPolicyRef.current, width, height);
    const next = cameraRef.current.aspect === aspect ? cameraRef.current : cameraRef.current.fitAspect(aspect, width, height, DEEP_ITERATIONS);
    cameraRef.current = next;
    if (!next.needsDeep(width, height, false)) {
      deepRef.current = false;
      deepImageVisibleRef.current = false;
      setDeep(false);
      report(null);
      if (canvasRef.current) canvasRef.current.style.transform = '';
      const ranges = next.toShallowRanges();
      const imageMatches = entry.imageData && entry.imageWidth === width && entry.imageHeight === height &&
        entry.imagePaletteKey === imageIdentityRef.current.paletteKey && entry.imageColorFunc === imageIdentityRef.current.colorFunc;
      if (!imageMatches || !restoreImage(ranges, entry.imageData)) presentShallow(ranges, { defer: true });
    } else presentRef.current({});
    if (limitError) report({ phase: 'limited', message: limitError.message });
  }, [cancelFrame, height, presentShallow, report, restoreImage, startHandoff, width]);

  const dimensions = useCallback(() => {
    const canvas = canvasRef.current;
    const rect = canvas.parentElement.getBoundingClientRect();
    const displayWidth = canvas.offsetWidth || width;
    const displayHeight = canvas.offsetHeight || height;
    // The viewport preview keeps its old aspect and centers the bitmap while
    // the section changes shape. Map gestures through that visible bitmap.
    return { left: rect.left + ((rect.width ?? displayWidth) - displayWidth) / 2,
      top: rect.top, width: displayWidth, height: displayHeight };
  }, [height, width]);

  const startPan = useCallback((x, y) => {
    finishZoom(true);
    wheelHistoryRef.current = false;
    panRef.current = { x, y, moved: false };
  }, [finishZoom]);

  const movePan = useCallback((x, y) => {
    const start = panRef.current;
    if (!start || (x === start.x && y === start.y)) return;
    if (!start.moved) saveHistory();
    start.moved = true;
    const size = dimensions();
    const operation = { type: 'pan', args: [start.x - x, start.y - y, size.width, size.height] };
    start.x = x;
    start.y = y;
    if (operate(operation)) queuePresent({ interactive: true, pan: true });
  }, [dimensions, operate, queuePresent, saveHistory]);

  const finishPan = useCallback(() => {
    const start = panRef.current;
    panRef.current = null;
    if (!start?.moved) return;
    cancelFrame();
    presentRef.current({ pan: true });
  }, [cancelFrame]);

  useEffect(() => {
    mountedRef.current = true;
    if (handoffRef.current) {
      suspend();
      startHandoff();
    } else if (cameraRef.current?.needsDeep(width, height, false)) {
      suspend();
      handoffRef.current = { snapshot: cameraRef.current.snapshot(), operations: [] };
      startHandoff();
    } else if (cameraRef.current?.shouldPrefetch(width, height)) {
      ensureRuntime().catch(() => {});
    }
    return () => {
      mountedRef.current = false;
      loadGenerationRef.current += 1;
      cancelFrame();
      controllerRef.current?.dispose();
      controllerRef.current = null;
      controllerCanvasRef.current = null;
      clearTimeout(handoffFadeRef.current);
    };
    // A mount owns one camera; prop replacement is handled independently below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const presetKey = JSON.stringify(initialSnapshot || [initialX, initialY]);
  const previousPresetRef = useRef(presetKey);
  useEffect(() => {
    if (previousPresetRef.current === presetKey) return;
    previousPresetRef.current = presetKey;
    cancelFrame();
    loadGenerationRef.current += 1;
    controllerRef.current?.cancel();
    historyRef.current = [];
    aspectPolicyRef.current = { aspect: initialSnapshot?.aspect || cameraFromRanges(initialX, initialY).aspect, ...propsRef.current };
    if (initialSnapshot) {
      handoffRef.current = { snapshot: initialSnapshot, operations: [] };
      startHandoff();
    } else {
      handoffRef.current = null;
      cameraRef.current = runtimeRef.current
        ? runtimeRef.current.decimalCameraFromSnapshot(cameraFromRanges(initialX, initialY).snapshot(),
          { ...propsRef.current, iterationBudget: DEEP_ITERATIONS })
        : cameraFromRanges(initialX, initialY);
      presentRef.current({});
    }
  }, [cancelFrame, initialSnapshot, initialX, initialY, presetKey, startHandoff]);

  const previousSizeRef = useRef({ width, height });
  useLayoutEffect(() => {
    const resized = previousSizeRef.current.width !== width || previousSizeRef.current.height !== height;
    previousSizeRef.current = { width, height };
    if (resized) operate({ type: 'aspect', args: [fittedAspect(aspectPolicyRef.current, width, height)] });
    if ((deepRef.current || resized) && cameraRef.current && !handoffRef.current) presentRef.current({});
  }, [colorParams, width, height, operate]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const keyDown = event => {
      if (event.key === 'Shift') setShiftPressed(true);
      if ((event.metaKey || event.ctrlKey) && event.key === 'z' && canvas.parentElement.matches(':hover')) {
        event.preventDefault();
        undo();
      }
    };
    const keyUp = event => { if (event.key === 'Shift') setShiftPressed(false); };
    const blur = () => { setShiftPressed(false); finishPan(); finishZoom(); };
    window.addEventListener('keydown', keyDown);
    window.addEventListener('keyup', keyUp);
    window.addEventListener('blur', blur);
    return () => {
      window.removeEventListener('keydown', keyDown);
      window.removeEventListener('keyup', keyUp);
      window.removeEventListener('blur', blur);
    };
  }, [canvasEpoch, finishPan, finishZoom, undo]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const surface = canvas.parentElement;
    const wheel = event => {
      if (!event.shiftKey && !event.ctrlKey) return;
      event.preventDefault();
      event.stopPropagation();
      if (!wheelHistoryRef.current) { saveHistory(); wheelHistoryRef.current = true; }
      const size = dimensions();
      const delta = Math.abs(event.deltaY) > Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
      // Browsers send trackpad pinches as ctrl+wheel. Restore their faster
      // response while keeping full mouse-wheel notches modest.
      const unit = event.deltaMode === 1 ? 40 : event.deltaMode === 2 ? 400 : 1;
      const rate = event.ctrlKey ? TRACKPAD_PINCH_ZOOM_RATE : WHEEL_ZOOM_RATE;
      const maxStep = event.ctrlKey ? MAX_TRACKPAD_PINCH_ZOOM_STEP : MAX_WHEEL_ZOOM_STEP;
      const amount = Math.max(-maxStep, Math.min(maxStep,
        (event.ctrlKey ? 1 : -1) * delta * unit * rate));
      const u = (event.clientX - size.left) / size.width;
      const v = (event.clientY - size.top) / size.height;
      if (operate({ type: 'zoom', args: [Math.exp(amount), u, v] })) {
        queuePresent({ interactive: true, anchor: [u, v] });
      }
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => { wheelHistoryRef.current = false; finishZoom(); }, 150);
    };
    const pinchPosition = touches => {
      const size = dimensions();
      const x = (touches[0].clientX + touches[1].clientX) / 2;
      const y = (touches[0].clientY + touches[1].clientY) / 2;
      return { x, y, u: (x - size.left) / size.width, v: (y - size.top) / size.height,
        distance: Math.hypot(touches[1].clientX - touches[0].clientX, touches[1].clientY - touches[0].clientY) || 1 };
    };
    const startTouch = event => {
      event.preventDefault();
      if (event.touches.length === 2) {
        finishPan();
        if (!wheelHistoryRef.current) { saveHistory(); wheelHistoryRef.current = true; }
        pinchRef.current = pinchPosition(event.touches);
      } else if (event.touches.length === 1) startPan(event.touches[0].clientX, event.touches[0].clientY);
    };
    const moveTouch = event => {
      event.preventDefault();
      if (event.touches.length === 2 && pinchRef.current) {
        const previous = pinchRef.current;
        const next = pinchPosition(event.touches);
        const factor = Math.max(0.25, Math.min(4, previous.distance / next.distance));
        operate({ type: 'zoom', args: [factor, previous.u, previous.v] });
        // Translation of the pinch midpoint keeps its original world anchor
        // under the fingers, including a changing midpoint while importing.
        if (next.x !== previous.x || next.y !== previous.y) {
          const size = dimensions();
          operate({ type: 'pan', args: [previous.x - next.x, previous.y - next.y, size.width, size.height] });
        }
        pinchRef.current = next;
        queuePresent({ interactive: true, anchor: [next.u, next.v] });
      } else if (event.touches.length === 1 && panRef.current) movePan(event.touches[0].clientX, event.touches[0].clientY);
    };
    const endTouch = event => {
      if (event.touches.length < 2 && pinchRef.current) {
        finishZoom();
        pinchRef.current = null;
        wheelHistoryRef.current = false;
        if (event.touches.length === 1) startPan(event.touches[0].clientX, event.touches[0].clientY);
      }
      if (!event.touches.length) finishPan();
    };
    const options = { passive: false };
    surface.addEventListener('wheel', wheel, options);
    surface.addEventListener('touchstart', startTouch, options);
    surface.addEventListener('touchmove', moveTouch, options);
    surface.addEventListener('touchend', endTouch, options);
    surface.addEventListener('touchcancel', endTouch, options);
    return () => {
      surface.removeEventListener('wheel', wheel);
      surface.removeEventListener('touchstart', startTouch);
      surface.removeEventListener('touchmove', moveTouch);
      surface.removeEventListener('touchend', endTouch);
      surface.removeEventListener('touchcancel', endTouch);
    };
  }, [canvasEpoch, dimensions, finishPan, finishZoom, movePan, operate, queuePresent, saveHistory, startPan]);

  useEffect(() => {
    if (typeof window === 'undefined' || !new URLSearchParams(window.location.search).has('deepZoomTest')) return;
    const canvas = canvasRef.current;
    Object.defineProperty(canvas, '__fractalCamera', { configurable: true, get: () => cameraRef.current?.snapshot() || handoffRef.current?.snapshot });
    Object.defineProperty(canvas, '__fractalDeepStatus', { configurable: true, get: () => statusRef.current });
    Object.defineProperty(canvas, '__fractalController', { configurable: true, get: () => controllerRef.current });
    canvas.__setFractalCamera = snapshot => {
      cancelFrame();
      controllerRef.current?.cancel();
      handoffRef.current = { snapshot, operations: [] };
      startHandoff();
    };
    return () => {
      delete canvas.__fractalCamera;
      delete canvas.__fractalDeepStatus;
      delete canvas.__fractalController;
      delete canvas.__setFractalCamera;
    };
  }, [canvasEpoch, cancelFrame, startHandoff]);

  const retry = () => {
    if (handoffRef.current) startHandoff(true);
    else if (controllerRef.current) controllerRef.current.retry();
    else presentRef.current({});
  };
  const phase = status?.phase;
  const canRequestDetail = !!controllerRef.current && (phase === 'limited' || (phase === 'complete' && status.unresolvedPixels > 0));
  const statusText = deepStatusText(status, canRequestDetail, width, height);
  // A backing-size change clears a canvas immediately. Keep the old shallow
  // bitmap through a pending deep handoff and its first crossfade, then resize
  // underneath the visible deep image. A handoff that ends in shallow mode
  // redraws after the new backing dimensions reach the DOM.
  const freezeShallowBacking = !!getPresentedSnapshot() &&
    (!!handoffRef.current ||
      deepRef.current && (!deepImageVisibleRef.current || handoffFadeRef.current !== null));
  if (freezeShallowBacking) backingFrozenRef.current = true;
  else {
    if (backingFrozenRef.current && !deepRef.current &&
        (shallowBackingRef.current.width !== width || shallowBackingRef.current.height !== height)) {
      refreshShallowAfterUnfreezeRef.current = true;
    }
    shallowBackingRef.current = { width, height };
    backingFrozenRef.current = false;
  }
  useLayoutEffect(() => {
    if (!refreshShallowAfterUnfreezeRef.current) return;
    refreshShallowAfterUnfreezeRef.current = false;
    if (mountedRef.current && !handoffRef.current && !deepRef.current) presentRef.current({});
  });
  return <figure>
    <div className="image-container"
        style={{ position: 'relative', overflow: 'hidden', cursor: shiftPressed ? 'zoom-in' : 'move', touchAction: 'none' }}
        onDoubleClick={event => {
          finishZoom(true);
          saveHistory();
          const size = dimensions();
          if (operate({ type: 'zoom', args: [1 / 1.2, (event.clientX - size.left) / size.width, (event.clientY - size.top) / size.height] })) presentRef.current({});
        }}
        onMouseDown={event => { if (event.button === 0) startPan(event.pageX, event.pageY); }}
        onMouseMove={event => movePan(event.pageX, event.pageY)}
        onMouseUp={event => { if (panRef.current) movePan(event.pageX, event.pageY); finishPan(); }}
        onMouseLeave={() => finishPan()}
      >
      <canvas key={canvasEpoch} ref={canvasRef}
        width={shallowBackingRef.current.width} height={shallowBackingRef.current.height}
        style={{ display: 'block', pointerEvents: 'none' }} />
      <canvas ref={deepCanvasRef} width={width} height={height} aria-hidden="true"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none',
          visibility: deep ? 'visible' : 'hidden', opacity: deep ? 1 : 0, transition: 'opacity 150ms ease-out' }} />
      {recovering && !deep && <div className="canvas-recovery" role="status">Restoring view…</div>}
    </div>
    {statusText && <figcaption role="status" style={{ fontSize: '0.85em' }}>
      {statusText}{' '}
      {(phase === 'error' || phase === 'limited') && <button type="button" onClick={retry}>Retry</button>}
      {canRequestDetail && <button type="button" onClick={() => controllerRef.current.moreDetail()}>More detail</button>}
    </figcaption>}
  </figure>;
}
